import { Input, Progress } from 'antd'
import { UploadFile } from 'antd/lib/upload/interface'
import classNames from 'classnames'
import { memo, useEffect, useRef, useState } from 'react'
import CSSMotion from 'rc-motion'
import { getBase64, trimFileExt } from '~/utils'

import styles from './UploadListItem.module.css'
import { MyBtn } from '~/components/ui/MyBtn'
import { FileType } from '~/configs'
import { FilePdfOutlined, FileWordOutlined } from '@ant-design/icons'
import { MyIcon } from '~/components/ui/MyIcon'

const _Item = ({
  file,
  filename,
  onRenameFile,
  onRemove,
  reUpload,
  disabled,
  canRename,
}: {
  file: UploadFile
  filename?: string
  onRenameFile: (uid: string, value: string) => void
  onRemove: (fid: string) => void
  reUpload: (file: UploadFile) => void
  disabled: boolean
  canRename: boolean
}) => {
  // Delay to show the progress bar
  const [showProgress, setShowProgress] = useState(false)
  const progressRafRef = useRef<any>()
  useEffect(() => {
    progressRafRef.current = setTimeout(() => {
      setShowProgress(true)
    }, 300)

    return () => {
      window.clearTimeout(progressRafRef.current)
    }
  }, [])

  const [base64Url, setBase64Url] = useState('')
  useEffect(() => {
    if (file.originFileObj) {
      getBase64(file.originFileObj, imgUrl => {
        setBase64Url(imgUrl)
      })
    } else if (file.url) {
      setBase64Url(file.url)
    }
  }, [file.originFileObj])

  const FilePreview = () => {
    if (file.status === 'error') {
      return (
        <div className="w-[152px] h-[152px] relative bg-neutral-200 rounded-lg flex flex-col items-center justify-center z-1">
          <span className="text-neutral-400">上传失败</span>
          <MyBtn
            className="mt-3 !leading-none"
            type="primary"
            size="small"
            onClick={() => reUpload(file)}
          >
            重新上传
          </MyBtn>
        </div>
      )
    }

    if ([FileType.docx, FileType.doc].includes(file.type as string)) {
      return (
        <div className="rounded-lg bg-neutral-200 flex items-center justify-center w-32 h-32 text-neutral-400 text-[24px]">
          <FileWordOutlined />
        </div>
      )
    }

    if (file.type === FileType.pdf) {
      return (
        <div className="rounded-lg bg-neutral-200 flex items-center justify-center w-32 h-32 text-neutral-400 text-[24px]">
          <FilePdfOutlined />
        </div>
      )
    }

    return (
      <img
        className={classNames(
          'rounded-2xl w-[152px] h-[152px] block object-cover'
        )}
        src={base64Url || '/images/placeholder.png'}
        alt=""
      />
    )
  }

  return (
    <div className="w-[152px]">
      <div className="rounded-2xl w-[152px] h-[152px] relative">
        {file.status !== 'uploading' && (
          <div className="relative">
            <div className="rounded-2xl w-[152px] h-[152px] block">
              <FilePreview />
            </div>
          </div>
        )}

        {showProgress && (
          <CSSMotion
            motionName={`antd-fade`}
            visible={(file.status as any) === 'uploading'}
            motionDeadline={300}
          >
            {({ className: motionClassName }: { className?: string }) => {
              // show loading icon if upload progress listener is disabled
              const loadingProgress =
                'percent' in file ? (
                  <Progress
                    type="line"
                    size="small"
                    status="active"
                    percent={file.percent}
                    showInfo={false}
                  />
                ) : null

              return (
                <div
                  className={classNames(motionClassName, styles.progressbar)}
                >
                  {loadingProgress}
                </div>
              )
            }}
          </CSSMotion>
        )}

        {/* delete item */}
        {!disabled && (
          <div className="absolute top-2 right-2 z-1">
            <span
              className="w-4 h-4 block cursor-pointer bg-neutral-50/50 rounded-full"
              onClick={() => onRemove(file.uid)}
            >
              <MyIcon icon="close-circle" className="text-[16px]" />
            </span>
          </div>
        )}
      </div>

      {canRename && (
        <div className="flex items-center mt-2">
          <span className="w-3.5 h-3.5 text-neutral-400 shrink-0 mr-1">
            <MyIcon icon="edit" className="text-[14px]" />
          </span>
          <Input
            className={classNames(
              'bg-transparent min-w-0 font-sm outline-none',
              styles.customInput
            )}
            value={(filename ?? trimFileExt(file.name)).slice(0, 20)}
            onChange={e => {
              onRenameFile(file.uid, e.target.value)
            }}
            maxLength={20}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  )
}

const UploadListItem = memo(_Item, (prev, next) => {
  if (
    prev.file.status === next.file.status &&
    prev.filename === next.filename &&
    prev.disabled === next.disabled &&
    prev.canRename === next.canRename
  ) {
    return true
  }
  return false
})

export default UploadListItem
